import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/website-designers-for-small-business-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/website-designers-for-small-business-colorado")({
  head: () => ({
    meta: [
      { title: "Website Designers for Small Business in Colorado: What to Look For (2026)" },
      { name: "description", content: "Choosing between website designers for small business in Colorado? What a small business site actually needs, honest prices, and the red flags that cost owners thousands." },
      { property: "og:title", content: "Website Designers for Small Business in Colorado: What to Look For (2026)" },
      { property: "og:description", content: "What a small business site actually needs, honest Colorado prices, and the red flags to avoid." },
      { property: "og:url", content: `${SITE_URL}/learn/website-designers-for-small-business-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Website Designers for Small Business",
        description: ARTICLE?.desc ?? "What to look for in a small business website designer in Colorado.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/website-designers-for-small-business-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Web Design"
      read="10 min read"
      title="Website designers for small business in Colorado: what to look for"
      intro="A small business website has one job: make the phone ring. Not win design awards, not show off animation. Here's what your site actually needs, what it should cost in Colorado, and the red flags that separate real designers from expensive hobbies."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Website designers for small business" },
      ]}
      path="/learn/website-designers-for-small-business-colorado"
      faqs={[
        { q: "What does a small business website actually need?", a: "Five things: it loads fast on a phone, it says what you do and where you do it in plain words, every page has an obvious way to call or request a quote, it's built so Google can rank it, and you own it outright. Everything else is decoration." },
        { q: "What should a small business pay for a website in Colorado?", a: "A real custom site runs $1,850 to $5,950 depending on page count. Our packages: Starter $1,850 (4 pages), Pro $2,750 (10 pages with foundation SEO), Expert $5,950 (up to 50 pages). All include a year of hosting, SSL, and a traffic dashboard, and most sites launch in under 7 business days." },
        { q: "Should a small business use a designer or a DIY builder?", a: "DIY builders work for a hobby or a proof-of-concept. For a business that depends on leads, the hidden costs — your time, slow templates, monthly fees forever, no real SEO — usually exceed a professional build within a year or two. Our custom vs. DIY guide breaks down the math." },
      ]}
    >
      <h2>What your site actually needs</h2>
      <ul>
        <li><strong>Speed on a phone.</strong> Most of your customers find you on mobile. If the site takes more than a couple of seconds to load, they're calling your competitor.</li>
        <li><strong>Plain-language answers.</strong> What you do, where you do it, what it costs or how to get a quote — above the fold, not buried.</li>
        <li><strong>An obvious next step.</strong> A phone number and a quote form on every page. If visitors have to hunt, they leave.</li>
        <li><strong>A structure Google can rank.</strong> A page per service, per city — not one &ldquo;services&rdquo; page listing twelve things.</li>
        <li><strong>Ownership.</strong> Your domain, your content, your site — in writing. If a designer &ldquo;leases&rdquo; you the site, walk away.</li>
      </ul>

      <h2>Red flags that cost owners thousands</h2>
      <ul>
        <li><strong>No published prices.</strong> &ldquo;Call for a quote&rdquo; often means the price depends on how much they think you have. We publish every price on our{" "}<Link to="/pricing">pricing page</Link>.</li>
        <li><strong>Long contracts.</strong> A designer confident in their work doesn't need to lock you in for a year. Read our{" "}<Link to="/learn/escape-marketing-agency-contracts-colorado">contract trap guide</Link>.</li>
        <li><strong>A portfolio with no rankings.</strong> Pretty screenshots are easy. Ask which of their clients actually show up on page one.</li>
        <li><strong>You can't reach the person doing the work.</strong> If every question goes through an account manager, expect every answer to take a week.</li>
      </ul>

      <h2>What it should cost</h2>
      <p>
        Honest Colorado pricing: <strong>$1,850</strong> for a focused 4-page site,{" "}
        <strong>$2,750</strong> for a 10-page site with foundation SEO and analytics done for
        you, <strong>$5,950</strong> for up to 50 pages built to rank service-by-city. Extra
        pages are $85 each, and every package includes a year of hosting, SSL, and a traffic
        dashboard. We've spent 27 years building sites for Colorado small businesses — see how we
        work on our <Link to="/colorado-web-design">Colorado web design page</Link>.
      </p>

      <h2>What to do next</h2>
      <p>
        See your new site before you pay for it — request a{" "}
        <Link to="/free-mockup">free website mockup</Link>, or read{" "}
        <Link to="/learn/custom-web-design-vs-diy-platforms">custom design vs. DIY platforms</Link>{" "}
        if you're still weighing the options.
      </p>
    </ArticleLayout>
  );
}
